/**
 * ====================================================================
 * FIKO CAD ARC DIMENSION
 * ====================================================================
 *
 * Yay özellikleri ölçüsü - Yarıçap, yay uzunluğu ve açı.
 * CATIA'nın yay ölçüm özelliklerine karşılık gelir.
 *
 * Özellikler:
 * - Yarıçap (R) gösterimi
 * - Yay uzunluğu gösterimi
 * - Merkez açısı (θ) gösterimi
 * - Başlangıç/bitiş noktaları
 * - Merkez işareti
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 * @license Commercial - Satılabilir Kalite
 */

import * as THREE from 'three';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js';
import { FikoSahne } from '../../FikoSahne';
import {
    Dimension,
    DimensionType,
    DimensionResult,
    DimensionState,
    GeometryReference,
    BaseDimensionOptions,
    ArrowStyle,
    DIMENSION_CONSTANTS,
    DimensionErrorType
} from './DimensionTypes';

// ====================================================================
// ARC DIMENSION OPTIONS
// ====================================================================

/**
 * Arc dimension için özel seçenekler
 */
export interface ArcDimensionOptions extends BaseDimensionOptions {
    /** Yarıçap göster */
    showRadius?: boolean;
    /** Yay uzunluğu göster */
    showArcLength?: boolean;
    /** Açı göster */
    showAngle?: boolean;
    /** Merkez işareti göster */
    showCenterMark?: boolean;
    /** Merkez işareti boyutu */
    centerMarkSize?: number;
    /** Açı formatı */
    angleFormat?: 'degrees' | 'radians' | 'dms';
    /** Açı sembolü (° veya rad) */
    showAngleSymbol?: boolean;
    /** Yay çizgisi göster */
    showArcOutline?: boolean;
    /** Leader line açısı */
    leaderAngle?: number;
}

/**
 * Yay hesaplama sonucu
 */
export interface ArcProperties {
    center: THREE.Vector3;
    radius: number;
    startAngle: number;       // Radyan
    endAngle: number;         // Radyan
    centralAngle: number;     // Radyan (toplam açı)
    arcLength: number;        // Birim uzunluk
    startPoint: THREE.Vector3;
    endPoint: THREE.Vector3;
    normal: THREE.Vector3;    // Yay düzlemi normali
}

// ====================================================================
// ARC DIMENSION CLASS
// ====================================================================

/**
 * Arc Dimension - Yay özellikleri ölçüsü
 */
export class ArcDimension extends Dimension {
    // Referans
    private ref: GeometryReference;

    // Hesaplanmış değerler
    private arcProps: ArcProperties;

    // Çizim elemanları
    private arcLine: LineSegments2 | null = null;
    private radiusLines: LineSegments2[] = [];
    private centerMark: THREE.Group | null = null;
    private angleArc: LineSegments2 | null = null;
    private labels: THREE.Sprite[] = [];
    private arrowMeshes: THREE.Mesh[] = [];

    // Pre-allocated vectors
    private readonly _tempVec = new THREE.Vector3();
    private readonly _dir1 = new THREE.Vector3();
    private readonly _dir2 = new THREE.Vector3();

    // Shared geometries
    private static _sharedArrowGeometry: THREE.BufferGeometry | null = null;

    // Sahne referansı
    private scene: FikoSahne;

    // SABİT BOYUT SİSTEMİ
    sabitBoyut = true;
    private hazirlaListenerEklendi = false;
    private kontrolDegistiEvent = () => this.kameraYonDegistiThrottled();

    // PERFORMANS
    private lastKameraUpdateTime = 0;
    private kameraUpdateTimeout: number | null = null;
    private static readonly KAMERA_THROTTLE_MS = 100;
    private targetScaleAdjustment = 1;
    private currentScaleAdjustment = 1;
    private interpolationRAF: number | null = null;
    private isDisposed = false;

    constructor(
        ref: GeometryReference,
        options: ArcDimensionOptions,
        scene: FikoSahne
    ) {
        super(DimensionType.ARC_LENGTH, options);

        this.ref = ref;
        this.scene = scene;

        // Default arc properties
        this.arcProps = {
            center: new THREE.Vector3(),
            radius: 0,
            startAngle: 0,
            endAngle: 0,
            centralAngle: 0,
            arcLength: 0,
            startPoint: new THREE.Vector3(),
            endPoint: new THREE.Vector3(),
            normal: new THREE.Vector3(0, 0, 1)
        };
    }

    /**
     * Dimension'ı oluştur
     */
    create(): DimensionResult {
        try {
            // Referanstan yay özelliklerini al
            if (!this.extractArcProperties()) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.INVALID_REFERENCE,
                        message: 'Yay özellikleri alınamadı (merkez, yarıçap, açılar gerekli)'
                    }
                };
            }

            // Değeri hesapla (yay uzunluğu)
            this._value = this.calculateValue();

            // Validate
            if (!this.validate()) {
                return {
                    success: false,
                    error: {
                        type: DimensionErrorType.INVALID_REFERENCE,
                        message: 'Yay çok küçük'
                    }
                };
            }

            // Geometriyi oluştur
            this.buildGeometry();

            // Sahneye hazırla
            this.hazirla();

            return {
                success: true,
                dimension: this
            };
        } catch (error) {
            return {
                success: false,
                error: {
                    type: DimensionErrorType.INTERNAL_ERROR,
                    message: `Arc dimension oluşturma hatası: ${error}`
                }
            };
        }
    }

    /**
     * Referanstan yay özelliklerini çıkar
     */
    private extractArcProperties(): boolean {
        const ref = this.ref;

        // Merkez ve yarıçap zorunlu
        if (!ref.center || ref.radius === undefined || ref.radius <= 0) {
            return false;
        }

        this.arcProps.center.copy(ref.center);
        this.arcProps.radius = ref.radius;

        // Açılar
        this.arcProps.startAngle = ref.startAngle ?? 0;
        this.arcProps.endAngle = ref.endAngle ?? Math.PI * 2;

        // Merkez açısı hesapla
        let centralAngle = this.arcProps.endAngle - this.arcProps.startAngle;
        if (centralAngle < 0) {
            centralAngle += Math.PI * 2;
        }
        this.arcProps.centralAngle = centralAngle;

        // Yay uzunluğu
        this.arcProps.arcLength = this.arcProps.radius * centralAngle;

        // Normal (yay düzlemi)
        if (ref.axis) {
            this.arcProps.normal.copy(ref.axis);
        } else if (ref.normal) {
            this.arcProps.normal.copy(ref.normal);
        }

        // Başlangıç ve bitiş noktaları hesapla
        this.calculateArcEndpoints();

        return true;
    }

    /**
     * Yay başlangıç ve bitiş noktalarını hesapla
     */
    private calculateArcEndpoints(): void {
        const { center, radius, startAngle, endAngle, normal } = this.arcProps;

        // Normal'a dik iki eksen bul
        let xAxis = new THREE.Vector3(1, 0, 0);
        if (Math.abs(normal.dot(xAxis)) > 0.9) {
            xAxis.set(0, 1, 0);
        }
        xAxis.crossVectors(normal, xAxis).normalize();
        const yAxis = new THREE.Vector3().crossVectors(xAxis, normal).normalize();

        // Başlangıç noktası
        this.arcProps.startPoint.copy(center)
            .addScaledVector(xAxis, Math.cos(startAngle) * radius)
            .addScaledVector(yAxis, Math.sin(startAngle) * radius);

        // Bitiş noktası
        this.arcProps.endPoint.copy(center)
            .addScaledVector(xAxis, Math.cos(endAngle) * radius)
            .addScaledVector(yAxis, Math.sin(endAngle) * radius);
    }

    /**
     * Dimension'ı güncelle
     */
    update(): void {
        // Scale güncelleme event listener üzerinden yapılır
    }

    /**
     * Sahneye hazırlama
     */
    hazirla(): void {
        if (!this.scene?.anaGosterim?.aktifKamera) return;

        try {
            if (this.labels.length > 0 && this.labels[0]?.userData.originalScale) {
                this.kameraYonDegisti();
            }

            if (!this.hazirlaListenerEklendi && this.scene.anaGosterim.aktifKontrol) {
                this.scene.anaGosterim.aktifKontrol.addEventListener("change", this.kontrolDegistiEvent);
                this.hazirlaListenerEklendi = true;
            }
        } catch (e) {
            // Sessizce devam et
        }
    }

    /**
     * Throttled kamera event handler
     */
    private kameraYonDegistiThrottled(): void {
        const now = performance.now();

        if (this.kameraUpdateTimeout !== null) return;

        if (now - this.lastKameraUpdateTime < ArcDimension.KAMERA_THROTTLE_MS) {
            this.kameraUpdateTimeout = window.setTimeout(() => {
                this.kameraUpdateTimeout = null;
                this.lastKameraUpdateTime = performance.now();
                this.kameraYonDegisti();
            }, ArcDimension.KAMERA_THROTTLE_MS - (now - this.lastKameraUpdateTime));
            return;
        }

        this.lastKameraUpdateTime = now;
        this.kameraYonDegisti();
    }

    /**
     * Kamera değişikliğinde scale güncelle
     */
    private kameraYonDegisti(): void {
        if (!this.scene?.anaGosterim?.aktifKamera) return;

        if (this.sabitBoyut && this.labels.length > 0) {
            const kamera = this.scene.anaGosterim.aktifKamera as any;
            const zoom = kamera?.zoom ?? 1;
            const factor = ((kamera?.top ?? 100) - (kamera?.bottom ?? -100)) / zoom;

            this.targetScaleAdjustment = factor * 0.02;
            this.startSmoothInterpolation();
        }
    }

    /**
     * Smooth scale interpolation
     */
    private startSmoothInterpolation(): void {
        if (this.interpolationRAF !== null) return;

        const interpolate = () => {
            if (this.labels.length === 0 || !this.sabitBoyut) {
                this.interpolationRAF = null;
                return;
            }

            const lerpFactor = 0.2;
            const diff = this.targetScaleAdjustment - this.currentScaleAdjustment;

            if (Math.abs(diff) < 0.001) {
                this.currentScaleAdjustment = this.targetScaleAdjustment;
                this.interpolationRAF = null;
                return;
            }

            this.currentScaleAdjustment += diff * lerpFactor;

            for (const label of this.labels) {
                if (label?.userData.originalScale) {
                    const originalScale = label.userData.originalScale as THREE.Vector3;
                    label.scale.set(
                        originalScale.x * this.currentScaleAdjustment,
                        originalScale.y * this.currentScaleAdjustment,
                        1
                    );
                }
            }

            this.interpolationRAF = requestAnimationFrame(interpolate);
        };

        this.interpolationRAF = requestAnimationFrame(interpolate);
    }

    /**
     * Validate
     */
    validate(): boolean {
        return this.arcProps.radius >= DIMENSION_CONSTANTS.MIN_RADIUS &&
               this.arcProps.centralAngle >= DIMENSION_CONSTANTS.MIN_ANGLE * (Math.PI / 180);
    }

    /**
     * Değeri hesapla (yay uzunluğu)
     */
    protected calculateValue(): number {
        return this.arcProps.arcLength;
    }

    /**
     * Geometriyi oluştur
     */
    protected buildGeometry(): void {
        this.clearGeometry();

        const options = this._options as ArcDimensionOptions;
        const color = options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        const lineWidth = options.lineWidth || DIMENSION_CONSTANTS.DEFAULT_LINE_WIDTH;
        const showRadius = options.showRadius !== false;
        const showArcLength = options.showArcLength !== false;
        const showAngle = options.showAngle !== false;
        const showCenterMark = options.showCenterMark !== false;
        const showArcOutline = options.showArcOutline !== false;

        const { center, radius, startAngle, endAngle, normal, startPoint, endPoint, centralAngle, arcLength } = this.arcProps;

        // Normal'a dik eksenler
        let xAxis = new THREE.Vector3(1, 0, 0);
        if (Math.abs(normal.dot(xAxis)) > 0.9) {
            xAxis.set(0, 1, 0);
        }
        xAxis.crossVectors(normal, xAxis).normalize();
        const yAxis = new THREE.Vector3().crossVectors(xAxis, normal).normalize();

        // Yay çizgisi
        if (showArcOutline) {
            this.arcLine = this.createArcLine(
                center, radius, startAngle, endAngle,
                xAxis, yAxis, color, lineWidth
            );
            this.arcLine.name = 'ArcOutline';
            this.add(this.arcLine);
        }

        // Yarıçap çizgileri (başlangıç ve bitiş)
        if (showRadius || showAngle) {
            const startRadiusLine = this.createLine(
                [center.x, center.y, center.z, startPoint.x, startPoint.y, startPoint.z],
                color, lineWidth * 0.7, 0.5
            );
            startRadiusLine.name = 'StartRadiusLine';
            this.radiusLines.push(startRadiusLine);
            this.add(startRadiusLine);

            const endRadiusLine = this.createLine(
                [center.x, center.y, center.z, endPoint.x, endPoint.y, endPoint.z],
                color, lineWidth * 0.7, 0.5
            );
            endRadiusLine.name = 'EndRadiusLine';
            this.radiusLines.push(endRadiusLine);
            this.add(endRadiusLine);
        }

        // Merkez işareti
        if (showCenterMark) {
            const centerMarkSize = options.centerMarkSize || 1;
            this.centerMark = this.createCenterMark(center, xAxis, yAxis, color, centerMarkSize);
            this.add(this.centerMark);
        }

        // Açı yayı (küçük gösterge yayı)
        if (showAngle) {
            const angleArcRadius = radius * 0.25;
            this.angleArc = this.createArcLine(
                center, angleArcRadius, startAngle, endAngle,
                xAxis, yAxis, 0x0066FF, lineWidth
            );
            this.angleArc.name = 'AngleArc';
            this.add(this.angleArc);
        }

        // Etiketler
        const midAngle = (startAngle + endAngle) / 2;
        const labelRadius = radius * 1.15;

        // Yarıçap etiketi
        if (showRadius) {
            const radiusLabelPos = center.clone()
                .addScaledVector(xAxis, Math.cos(midAngle) * (radius * 0.5))
                .addScaledVector(yAxis, Math.sin(midAngle) * (radius * 0.5));

            const radiusLabel = this.createLabel(
                `R${radius.toFixed(options.precision || 2)}`,
                radiusLabelPos,
                '#E53935' // Kırmızı
            );
            radiusLabel.name = 'RadiusLabel';
            this.labels.push(radiusLabel);
            this.add(radiusLabel);
        }

        // Yay uzunluğu etiketi
        if (showArcLength) {
            const arcLabelPos = center.clone()
                .addScaledVector(xAxis, Math.cos(midAngle) * labelRadius)
                .addScaledVector(yAxis, Math.sin(midAngle) * labelRadius);

            const unit = options.unit || 'mm';
            const arcLabel = this.createLabel(
                `${arcLength.toFixed(options.precision || 2)} ${unit}`,
                arcLabelPos,
                '#000000' // Siyah
            );
            arcLabel.name = 'ArcLengthLabel';
            this.labels.push(arcLabel);
            this.add(arcLabel);
        }

        // Açı etiketi
        if (showAngle) {
            const angleLabelPos = center.clone()
                .addScaledVector(xAxis, Math.cos(midAngle) * (radius * 0.35))
                .addScaledVector(yAxis, Math.sin(midAngle) * (radius * 0.35));

            const angleText = this.formatAngle(centralAngle, options);
            const angleLabel = this.createLabel(
                angleText,
                angleLabelPos,
                '#0066FF' // Mavi
            );
            angleLabel.name = 'AngleLabel';
            this.labels.push(angleLabel);
            this.add(angleLabel);
        }

        // Oklar (yay uçlarında)
        const arrowSize = options.arrowSize || 0.4;

        // Başlangıç oku
        this._dir1.copy(startPoint).sub(center).normalize();
        const startTangent = new THREE.Vector3().crossVectors(normal, this._dir1).normalize();
        const startArrow = this.createArrow(startPoint, startTangent, color, arrowSize);
        startArrow.name = 'StartArrow';
        this.arrowMeshes.push(startArrow);
        this.add(startArrow);

        // Bitiş oku
        this._dir2.copy(endPoint).sub(center).normalize();
        const endTangent = new THREE.Vector3().crossVectors(this._dir2, normal).normalize();
        const endArrow = this.createArrow(endPoint, endTangent, color, arrowSize);
        endArrow.name = 'EndArrow';
        this.arrowMeshes.push(endArrow);
        this.add(endArrow);
    }

    /**
     * Yay çizgisi oluştur
     */
    private createArcLine(
        center: THREE.Vector3,
        radius: number,
        startAngle: number,
        endAngle: number,
        xAxis: THREE.Vector3,
        yAxis: THREE.Vector3,
        color: number,
        lineWidth: number
    ): LineSegments2 {
        const segments = 32;
        const positions: number[] = [];

        let angle = startAngle;
        const angleStep = (endAngle - startAngle) / segments;

        for (let i = 0; i <= segments; i++) {
            const point = center.clone()
                .addScaledVector(xAxis, Math.cos(angle) * radius)
                .addScaledVector(yAxis, Math.sin(angle) * radius);
            positions.push(point.x, point.y, point.z);
            angle += angleStep;
        }

        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color,
            linewidth: lineWidth,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            fog: false,
            worldUnits: false,
            toneMapped: false
        });

        const line = new LineSegments2(geometry, material);
        line.renderOrder = Infinity;
        line.frustumCulled = false;
        line.computeLineDistances();

        return line;
    }

    /**
     * Düz çizgi oluştur
     */
    private createLine(
        positions: number[],
        color: number,
        lineWidth: number,
        opacity: number = 1
    ): LineSegments2 {
        const geometry = new LineGeometry();
        geometry.setPositions(positions);

        const material = new LineMaterial({
            color,
            linewidth: lineWidth,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity,
            fog: false,
            worldUnits: false,
            toneMapped: false
        });

        const line = new LineSegments2(geometry, material);
        line.renderOrder = Infinity;
        line.frustumCulled = false;
        line.computeLineDistances();

        return line;
    }

    /**
     * Merkez işareti oluştur
     */
    private createCenterMark(
        center: THREE.Vector3,
        xAxis: THREE.Vector3,
        yAxis: THREE.Vector3,
        color: number,
        size: number
    ): THREE.Group {
        const group = new THREE.Group();
        group.name = 'CenterMark';

        // Cross
        const line1 = this.createLine(
            [
                center.x - xAxis.x * size, center.y - xAxis.y * size, center.z - xAxis.z * size,
                center.x + xAxis.x * size, center.y + xAxis.y * size, center.z + xAxis.z * size
            ],
            color, 0.5
        );
        group.add(line1);

        const line2 = this.createLine(
            [
                center.x - yAxis.x * size, center.y - yAxis.y * size, center.z - yAxis.z * size,
                center.x + yAxis.x * size, center.y + yAxis.y * size, center.z + yAxis.z * size
            ],
            color, 0.5
        );
        group.add(line2);

        return group;
    }

    /**
     * Açıyı formatla
     */
    private formatAngle(radians: number, options: ArcDimensionOptions): string {
        const format = options.angleFormat || 'degrees';
        const showSymbol = options.showAngleSymbol !== false;

        if (format === 'radians') {
            const value = radians.toFixed(options.precision || 3);
            return showSymbol ? `${value} rad` : value;
        }

        if (format === 'dms') {
            const degrees = radians * (180 / Math.PI);
            const d = Math.floor(degrees);
            const m = Math.floor((degrees - d) * 60);
            const s = ((degrees - d) * 60 - m) * 60;
            return `${d}° ${m}' ${s.toFixed(1)}"`;
        }

        // Degrees (default)
        const degrees = radians * (180 / Math.PI);
        return showSymbol ? `${degrees.toFixed(options.precision || 1)}°` : degrees.toFixed(options.precision || 1);
    }

    /**
     * Ok oluştur
     */
    private createArrow(
        position: THREE.Vector3,
        direction: THREE.Vector3,
        color: number,
        size: number
    ): THREE.Mesh {
        if (!ArcDimension._sharedArrowGeometry) {
            ArcDimension._sharedArrowGeometry = new THREE.ConeGeometry(0.3, 0.8, 8);
            ArcDimension._sharedArrowGeometry.translate(0, -0.4, 0);
        }

        const material = new THREE.MeshBasicMaterial({
            color,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            side: THREE.DoubleSide,
            toneMapped: false
        });

        const arrow = new THREE.Mesh(ArcDimension._sharedArrowGeometry, material);
        arrow.position.copy(position);
        arrow.scale.setScalar(size);

        const quaternion = new THREE.Quaternion();
        quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);
        arrow.quaternion.copy(quaternion);

        arrow.renderOrder = Infinity;
        arrow.frustumCulled = false;

        return arrow;
    }

    /**
     * Label oluştur
     */
    private createLabel(text: string, position: THREE.Vector3, textColor: string): THREE.Sprite {
        const fontSize = this._options.fontSize || DIMENSION_CONSTANTS.DEFAULT_FONT_SIZE;

        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        const metrics = ctx.measureText(text);
        const textWidth = metrics.width;
        const textHeight = fontSize * 4;

        const padding = 16;
        canvas.width = textWidth + padding * 2;
        canvas.height = textHeight + padding * 2;

        // Arka plan
        ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
        ctx.roundRect(4, 4, canvas.width - 8, canvas.height - 8, 4);
        ctx.fill();

        // Metin
        ctx.font = `bold ${fontSize * 4}px Arial, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.shadowColor = 'rgba(255, 255, 255, 0.95)';
        ctx.shadowBlur = 2;
        ctx.fillStyle = textColor;
        ctx.fillText(text, canvas.width / 2, canvas.height / 2);

        const texture = new THREE.CanvasTexture(canvas);
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;

        const spriteMaterial = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            depthTest: false,
            depthWrite: false,
            sizeAttenuation: true,
            toneMapped: false
        });

        const sprite = new THREE.Sprite(spriteMaterial);
        sprite.position.copy(position);
        sprite.scale.set(canvas.width * 0.01, canvas.height * 0.01, 1);
        sprite.renderOrder = Infinity;
        sprite.frustumCulled = false;

        sprite.userData.originalScale = new THREE.Vector3().copy(sprite.scale);

        return sprite;
    }

    /**
     * Görünümü güncelle
     */
    protected updateAppearance(): void {
        let color: number;

        switch (this._state) {
            case DimensionState.HOVERED:
                color = DIMENSION_CONSTANTS.COLORS.HOVERED;
                break;
            case DimensionState.SELECTED:
                color = DIMENSION_CONSTANTS.COLORS.SELECTED;
                break;
            default:
                color = this._options.color || DIMENSION_CONSTANTS.COLORS.NORMAL;
        }

        if (this.arcLine) {
            (this.arcLine.material as LineMaterial).color.setHex(color);
        }

        for (const line of this.radiusLines) {
            (line.material as LineMaterial).color.setHex(color);
        }

        for (const arrow of this.arrowMeshes) {
            (arrow.material as THREE.MeshBasicMaterial).color.setHex(color);
        }
    }

    /**
     * Geometriyi temizle
     */
    private clearGeometry(): void {
        if (this.arcLine) {
            this.remove(this.arcLine);
            this.arcLine.geometry.dispose();
            (this.arcLine.material as LineMaterial).dispose();
            this.arcLine = null;
        }

        for (const line of this.radiusLines) {
            this.remove(line);
            line.geometry.dispose();
            (line.material as LineMaterial).dispose();
        }
        this.radiusLines = [];

        if (this.centerMark) {
            this.centerMark.traverse((child) => {
                if (child instanceof LineSegments2) {
                    child.geometry.dispose();
                    (child.material as LineMaterial).dispose();
                }
            });
            this.remove(this.centerMark);
            this.centerMark = null;
        }

        if (this.angleArc) {
            this.remove(this.angleArc);
            this.angleArc.geometry.dispose();
            (this.angleArc.material as LineMaterial).dispose();
            this.angleArc = null;
        }

        for (const label of this.labels) {
            this.remove(label);
            label.material.map?.dispose();
            label.material.dispose();
        }
        this.labels = [];

        for (const arrow of this.arrowMeshes) {
            this.remove(arrow);
            (arrow.material as THREE.Material).dispose();
        }
        this.arrowMeshes = [];
    }

    // ====================================================================
    // PUBLIC API
    // ====================================================================

    /**
     * Yay özelliklerini al
     */
    getArcProperties(): ArcProperties {
        return { ...this.arcProps };
    }

    /**
     * Yarıçapı al
     */
    getRadius(): number {
        return this.arcProps.radius;
    }

    /**
     * Yay uzunluğunu al
     */
    getArcLength(): number {
        return this.arcProps.arcLength;
    }

    /**
     * Merkez açıyı al (derece)
     */
    getCentralAngleDegrees(): number {
        return this.arcProps.centralAngle * (180 / Math.PI);
    }

    /**
     * Merkez açıyı al (radyan)
     */
    getCentralAngleRadians(): number {
        return this.arcProps.centralAngle;
    }

    /**
     * Dispose
     */
    override dispose(): void {
        if (this.isDisposed) return;
        this.isDisposed = true;

        if (this.kameraUpdateTimeout !== null) {
            clearTimeout(this.kameraUpdateTimeout);
            this.kameraUpdateTimeout = null;
        }

        if (this.interpolationRAF !== null) {
            cancelAnimationFrame(this.interpolationRAF);
            this.interpolationRAF = null;
        }

        if (this.scene?.anaGosterim?.aktifKontrol && this.hazirlaListenerEklendi) {
            this.scene.anaGosterim.aktifKontrol.removeEventListener("change", this.kontrolDegistiEvent);
            this.hazirlaListenerEklendi = false;
        }

        this.clearGeometry();
        super.dispose();
    }
}
